import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "@workos-inc/authkit-react";
import { useEffect, useRef, useState } from "react";

import { authRedirectState } from "@/lib/auth-return-to";
import { captureUserActionError } from "@/lib/sentry";
import { usePageRestore } from "@/lib/use-page-restore";
import { AuthLoadingScreen } from "./auth-loading-screen";
import { SignInScreen } from "./sign-in-screen";

/** The WorkOS sign-in endpoint (initiate_login_uri) configured for this app. */
export const SIGN_IN_ENDPOINT_PATH = "/login";

/**
 * WorkOS sends users to the sign-in endpoint (e.g. from its hosted invitation link) and keeps the
 * pending invitation on its side, so the endpoint must start AuthKit right away, even when already
 * signed in, for WorkOS to apply it.
 */
export function SignInEndpointScreen({ isAuthenticated }: { isAuthenticated: boolean }) {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  // Back from AuthKit restores this page from bfcache mid-redirect. Redirecting again would trap
  // Back, so leave the endpoint as a failed redirect would.
  usePageRestore(() => {
    if (isAuthenticated) void navigate({ to: "/", replace: true });
    else setFailed(true);
  });

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    signIn({ state: authRedirectState() }).catch((error: unknown) => {
      captureUserActionError(error, "auth.signInEndpointRedirect");
      if (isAuthenticated) {
        void navigate({ to: "/", replace: true });
      } else {
        setFailed(true);
      }
    });
  }, [isAuthenticated, navigate, signIn]);

  return failed ? <SignInScreen /> : <AuthLoadingScreen />;
}
